import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GoogleMap, GoogleMapsModule } from '@angular/google-maps';
import { EventTemplateLayout } from '@models/design-templates/event-template-layout.enum';
import { EventSectionLayoutType } from '@models/events/event-section-layout-type.enum';
import { EventSectionMap } from '@models/events/event-section-map.model';
import { EventSection } from '@models/events/event-section.model';
import { EventType } from '@models/events/event-type.enum';
import { Event } from '@models/events/event.model';
import { Address } from '@models/shared/address.model';
import { TranslateModule } from '@ngx-translate/core';
import { EventStore } from '@services/stores/event.store';

@Component({
  selector: 'app-event-section-map',
  standalone: true,
  imports: [CommonModule, GoogleMapsModule, TranslateModule],
  templateUrl: './event-section-map.component.html',
  styleUrl: './event-section-map.component.scss',
})
export class EventSectionMapComponent implements OnInit, AfterViewInit {
  @Input() disableActions?: boolean = false;
  @Input() editView?: boolean = false;
  @Input() bodyColor?: string;
  @Input() sectionMap?: EventSectionMap;
  @Input() section?: EventSection;

  mapZoom = 13;
  mapCenter: google.maps.LatLngLiteral = {
    lat: 52.52,
    lng: 13.4,
  };
  markerPosition = this.mapCenter;
  markerPositionMap = new Map();
  mapOptions: google.maps.MapOptions = {
    mapTypeId: google.maps.MapTypeId.ROADMAP,
    zoomControl: true,
    scrollwheel: true,
    disableDoubleClickZoom: true,
    maxZoom: 20,
    minZoom: 4,
    draggableCursor: 'default',
    styles: [
      {
        featureType: 'all',
        elementType: 'all',
        stylers: [{ saturation: -100 }],
      },
    ],
  };
  mapTypeId = 'tehgrayz';
  mapTypeOptions = {
    name: 'Grayscale',
  };
  noActionsMapOptions: google.maps.MapOptions = {
    disableDefaultUI: true,
    zoomControl: false,
    mapTypeControl: false,
    scaleControl: false,
    streetViewControl: false,
    rotateControl: false,
    fullscreenControl: false,
  };

  styles: google.maps.MapTypeStyle[] = [
    {
      featureType: 'all',
      elementType: 'all',
      stylers: [{ saturation: -100 }],
    },
  ];
  ngAfterViewInit(): void {
    if (
      this.map &&
      (this.event?.type === EventType.SPORTPAGE ||
        this.event?.type === EventType.LANDINGPAGE ||
        this.event?.isGeneric())
    ) {
      const googleMap = this.map.googleMap;

      if (this.section?.layout === EventSectionLayoutType.CAMPUS_FOUNDERS) {
        this.styles = [
          {
            featureType: 'all',
            elementType: 'all',
            stylers: [{ saturation: -100 }, { gamma: 0.1 }],
          },
        ];
      }

      const styledMapType = new google.maps.StyledMapType(
        this.styles,
        this.mapTypeOptions,
      );
      googleMap?.mapTypes.set(this.mapTypeId, styledMapType);
      googleMap?.setMapTypeId(this.mapTypeId);
    }
  }

  markerOptions: google.maps.marker.AdvancedMarkerElementOptions = {
    gmpDraggable: false,
  };

  loading = false;
  skipFindCoordinates = false;
  isEventEditPicker = false;

  @ViewChild(GoogleMap, { static: false }) map?: GoogleMap;

  event?: Event;
  events?: Event[];
  isDarkTheme: boolean = false;

  sectionMapAddresses?: Address[];

  constructor(private eventStore: EventStore) {
    this.eventStore.event.pipe(takeUntilDestroyed()).subscribe((event) => {
      if (event) {
        this.event = event;
        if (this.event?.eventAddress) {
          this.markerPosition = {
            lat: this.event.eventAddress.lat,
            lng: this.event.eventAddress.lng,
          };
          this.mapCenter = this.markerPosition;

          if (
            this.event.eventAddress.isFilled() ||
            this.event.eventAddress.locality === 'Berlin'
          ) {
            this.mapZoom = 17;
          } else {
            this.mapZoom = 12;
          }
        }
      }
    });
  }

  ngOnInit(): void {
    if (this.sectionMap) {
      this.sectionMapAddresses = this.sectionMap.getFilledAddresses();
      this.sectionMapAddresses.forEach((address, i) => {
        const markerPosition = {
          lat: address.lat,
          lng: address.lng,
        };
        this.markerPositionMap.set(address.id, markerPosition);

        if (i === 0) {
          this.mapCenter = markerPosition;
        }
      });

      this.mapZoom = 6;
    } else {
      if (this.event?.type === EventType.LANDINGPAGE) {
        this.events = this.event.getLandingPageEvents();
        this.isDarkTheme = true;
        if (this.events) {
          this.events.forEach((x, i) => {
            if (x.eventAddress) {
              const markerPosition = {
                lat: x.eventAddress?.lat,
                lng: x.eventAddress?.lng,
              };
              this.markerPositionMap.set(x.id, markerPosition);

              if (i === 0) {
                this.mapCenter = markerPosition;
              }
            }
          });

          this.mapZoom = 6;
        }
      }

      if (this.event?.type === EventType.ADVANCED_ONE_COLUMN) {
        this.mapOptions.scrollwheel = false;
      }
    }
  }

  getPosition(eventId: number) {
    return this.markerPositionMap.get(eventId);
  }

  openGoogleMaps() {
    if (
      !this.event ||
      (!this.event.eventAddress && !this.events) ||
      this.editView
    ) {
      return;
    }

    if (this.events && this.events.length > 0) {
      window.open(this.events[0].getGoogleMapsLink(), '_blank');
    } else {
      window.open(this.event.getGoogleMapsLink(), '_blank');
    }
  }

  getMarkerOptions(): google.maps.marker.AdvancedMarkerElementOptions {
    if (this.isSportGenericEvent) {
      const markerImg = document.createElement('img');
      markerImg.src =
        'https://static.eventpage.ai/temp/mizuno/images/retailer-location-pin.png';
      markerImg.style.width = '50px';
      markerImg.style.height = '50px';
      return {
        gmpClickable: true,
        gmpDraggable: false,
        content: markerImg,
      };
    } else if (
      this.isUaSection ||
      this.event?.type === EventType.ADVANCED_ONE_COLUMN
    ) {
      const markerImg = document.createElement('img');
      markerImg.src =
        'https://static.eventpage.ai/event-templates/event-templates-1594/map-pin.png';
      markerImg.style.width = '40px';
      markerImg.style.height = '40px';
      return {
        gmpDraggable: false,
        content: markerImg,
      };
    } else if (
      this.section?.layout === EventSectionLayoutType.CAMPUS_FOUNDERS
    ) {
      const markerImg = document.createElement('i');
      markerImg.className = 'fas fa-map-marker-alt';
      markerImg.style.fontSize = '40px';
      markerImg.style.color = '#ffffff';
      markerImg.style.lineHeight = '1';

      return {
        gmpDraggable: false,
        content: markerImg,
      };
    } else {
      const markerImg = document.createElement('img');
      markerImg.src = '/assets/pink-marker.png';
      markerImg.style.width = '30px';
      markerImg.style.height = '40px';
      return {
        gmpDraggable: false,
        content: markerImg,
      };
    }
  }

  onMarkerClick(address: Address) {
    window.open(address.getGoogleMapsLink(), '_blank');
  }

  onMapReady(map: google.maps.Map) {
    if (this.section?.layout === EventSectionLayoutType.CAMPUS_FOUNDERS) {
      class FullscreenTintOverlay extends google.maps.OverlayView {
        private layerDiv: HTMLDivElement | null = null;
        constructor(private color: string) {
          super();
        }

        override onAdd() {
          this.layerDiv = document.createElement('div');
          this.layerDiv.style.position = 'absolute';
          this.layerDiv.style.pointerEvents = 'none';
          this.layerDiv.style.background = this.color;
          this.layerDiv.style.backgroundBlendMode = 'plus-darker, normal';
          this.getPanes()?.overlayLayer.appendChild(this.layerDiv);
        }

        override draw() {
          if (!this.layerDiv) return;
          const proj = this.getProjection();
          if (!proj) return;

          const gmap = this.getMap() as google.maps.Map;
          const bounds = gmap.getBounds();
          if (!bounds) return;

          const ne = bounds.getNorthEast();
          const sw = bounds.getSouthWest();

          const topLeft = proj.fromLatLngToDivPixel(
            new google.maps.LatLng(ne.lat(), sw.lng()),
          );
          const bottomRight = proj.fromLatLngToDivPixel(
            new google.maps.LatLng(sw.lat(), ne.lng()),
          );

          if (!topLeft || !bottomRight) return; // projection not ready yet

          const width = bottomRight.x - topLeft.x;
          const height = bottomRight.y - topLeft.y;
          if (width <= 0 || height <= 0) return;

          this.layerDiv.style.left = `${topLeft.x - 250}px`;
          this.layerDiv.style.top = `${topLeft.y - 250}px`;
          this.layerDiv.style.width = `${width + 500}px`;
          this.layerDiv.style.height = `${height + 500}px`;
        }

        override onRemove() {
          this.layerDiv?.remove();
          this.layerDiv = null;
        }
      }

      const overlay = new FullscreenTintOverlay(
        'var(--event-theme-primary-color-opacity-60)',
      );
      overlay.setMap(map);
      google.maps.event.addListenerOnce(map, 'idle', () => overlay.draw());
    }
  }

  get isUaSection(): boolean {
    return !!this.event?.uri.includes('underarmour-infinite-elite-testival');
  }

  get isSportGenericEvent() {
    return (
      this.event?.isGeneric() && this.event?.uri.includes('for-every-run-tour')
    );
  }

  protected readonly Event = Event;
  protected readonly EventType = EventType;
  protected readonly EventTemplateLayout = EventTemplateLayout;
  protected readonly EventSectionLayoutType = EventSectionLayoutType;
}
